import axios from 'axios';
import SparkMD5 from 'spark-md5';
import {
  ApplicationHandleResult,
  CbEvents,
  getSDK,
  GroupJoinSource,
  GroupMemberFilter,
  GroupMemberRole,
  GroupType,
  GroupVerificationType,
  MessageStatus,
  MessageType,
  MessageReceiveOptType,
  SessionType,
  ViewType
} from '@openim/wasm-client-sdk';
import { appConfig } from '@/config';
import { toPortableObjectPath } from '@/utils/assetUrl';
import {
  applyRecallEvent,
  messageKeys,
  preserveRecalledState,
  shouldHideRecalledMessage,
  withRecalledState
} from '@/utils/messageRecall.mjs';
import {
  classifyGroupMessageReceipts,
  groupReceiptMessageIdentity
} from '@/utils/groupMessageReceipt.mjs';
import { unwrapSdkMessage, unwrapSdkResponse } from '@/utils/sdkResponse.mjs';

const LOCAL_MESSAGE_STORAGE_KEY = 'jc-openim-local-messages-v1';
const LOCAL_HIDDEN_MESSAGE_STORAGE_KEY = 'jc-openim-hidden-messages-v1';
const LOCAL_RECALLED_MESSAGE_STORAGE_KEY = 'jc-openim-recalled-messages-v1';
// Application-level edit event shared by future clients; the OpenIM 3.8.3 SDK has no sent-message edit API.
const JC_MESSAGE_EDIT_TYPE = 'jc.message.edit.v1';
const JC_MESSAGE_EDIT_DESCRIPTION = 'JC_MESSAGE_EDIT_V1';
const JC_GROUP_MESSAGE_ACTION_SETTINGS_KEY = 'jcMessageActionSettingsV1';
const JC_GROUP_LEGACY_EXTENSION_KEY = 'jcLegacyExtension';
const JC_GROUP_MESSAGE_RECEIPT_ENDPOINT = '/business-api/message-receipts.php';
const JC_BOT_MANAGEMENT_ENDPOINT = '/business-api/bots.php';
const SESSION_STORAGE_KEY = 'qmt-web-im-session';
const MAX_MESSAGE_ACTION_LIMIT_MINUTES = 525600;

function storedManagementToken() {
  if (typeof localStorage === 'undefined') return '';
  try {
    const session = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
    if (session.appServer !== appConfig.appServer || Number(session.platform) !== Number(appConfig.platform)) return '';
    return String(session.token || '').trim();
  } catch {
    return '';
  }
}

function randomId(prefix = '') {
  const value = window.crypto?.randomUUID
    ? window.crypto.randomUUID().replaceAll('-', '')
    : `${Date.now()}${Math.random().toString(16).slice(2)}`;
  return `${prefix}${value}`;
}

async function createTextMessageFromWasm(text) {
  if (typeof window.createTextMessage !== 'function') {
    throw new Error('消息创建服务未就绪');
  }
  const response = await window.createTextMessage(randomId('jc-create-text-'), text);
  return unwrapSdkMessage(response, '消息创建失败');
}

function withTimeout(promise, timeoutMs, message) {
  let timer = 0;
  const timeout = new Promise((resolve, reject) => {
    timer = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));
}

function scopedStorageKey(baseKey, userId = '') {
  const scope = `${appConfig.appServer || ''}:${userId || 'anonymous'}`;
  return `${baseKey}:${encodeURIComponent(scope)}`;
}

function readJsonStorage(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
  } catch (error) {
    return fallback;
  }
}

function writeJsonStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function readLocalMessages(userId) {
  return readJsonStorage(scopedStorageKey(LOCAL_MESSAGE_STORAGE_KEY, userId), {});
}

function writeLocalMessages(messagesByConversation, userId) {
  writeJsonStorage(scopedStorageKey(LOCAL_MESSAGE_STORAGE_KEY, userId), messagesByConversation);
}

function clearLocalConversationMessages(conversationId, userId) {
  const messagesByConversation = readLocalMessages(userId);
  delete messagesByConversation[conversationId];
  writeLocalMessages(messagesByConversation, userId);
  [LOCAL_HIDDEN_MESSAGE_STORAGE_KEY, LOCAL_RECALLED_MESSAGE_STORAGE_KEY].forEach(storageKey => {
    const values = readMessageSet(storageKey, userId);
    delete values[conversationId];
    writeMessageSet(storageKey, values, userId);
  });
}

function readMessageSet(storageKey, userId) {
  return readJsonStorage(scopedStorageKey(storageKey, userId), {});
}

function writeMessageSet(storageKey, messagesByConversation, userId) {
  writeJsonStorage(scopedStorageKey(storageKey, userId), messagesByConversation);
}

function messageEditPayload(message) {
  if (Number(message?.contentType) !== MessageType.CustomMessage
    || message?.customElem?.description !== JC_MESSAGE_EDIT_DESCRIPTION) {
    return null;
  }
  const payload = parseJsonValue(message.customElem.data, null);
  if (payload?.type !== JC_MESSAGE_EDIT_TYPE || typeof payload?.text !== 'string') {
    return null;
  }
  const targetIds = [payload.targetClientMsgID, payload.targetServerMsgID]
    .filter(Boolean)
    .map(value => String(value));
  if (!targetIds.length) {
    return null;
  }
  return {
    targetIds,
    text: payload.text,
    originalText: typeof payload.originalText === 'string' ? payload.originalText : '',
    editedAt: Number(payload.editedAt || message.sendTime || message.createTime || Date.now())
  };
}

function applyMessageEditEvents(messages) {
  const editEvents = (messages || [])
    .filter(message => message?.type === 'message_edit')
    .sort((left, right) => Number(left.editedAt || 0) - Number(right.editedAt || 0));
  const visibleMessages = (messages || []).filter(message => message?.type !== 'message_edit');
  editEvents.forEach(editEvent => {
    const targetIds = new Set((editEvent.editTargetIds || []).map(value => String(value)));
    const targetIndex = visibleMessages.findIndex(message => {
      return messageKeys(message).some(key => targetIds.has(String(key)));
    });
    if (targetIndex < 0) {
      return;
    }
    const target = visibleMessages[targetIndex];
    if (Number(target.editedAt || 0) > Number(editEvent.editedAt || 0)) {
      return;
    }
    visibleMessages.splice(targetIndex, 1, {
      ...target,
      originalText: target.originalText || editEvent.originalText || target.text,
      text: editEvent.text,
      editedAt: Number(editEvent.editedAt || 0),
      activityTimestamp: Number(editEvent.editedAt || 0),
      editEventId: editEvent.messageUid || editEvent.id || ''
    });
  });
  return visibleMessages;
}

function searchableMessageText(message) {
  return [
    message?.text,
    message?.originalText,
    message?.senderName,
    message?.from,
    message?.attachment?.name,
    message?.recallStatus
  ].filter(Boolean).join('\n').toLocaleLowerCase();
}

function storeMessageKeys(storageKey, conversationId, messages, userId) {
  const stored = readMessageSet(storageKey, userId);
  const ids = new Set(stored[conversationId] || []);
  (Array.isArray(messages) ? messages : [messages]).forEach(message => {
    messageKeys(message).forEach(id => ids.add(id));
  });
  stored[conversationId] = Array.from(ids);
  writeMessageSet(storageKey, stored, userId);
}

function hasStoredMessage(storageKey, conversationId, message, userId) {
  const stored = readMessageSet(storageKey, userId);
  const ids = new Set(stored[conversationId] || []);
  return messageKeys(message).some(id => ids.has(id));
}

function applyLocalMessageState(conversationId, messages, userId) {
  return applyMessageEditEvents(messages)
    .filter(message => !hasStoredMessage(LOCAL_HIDDEN_MESSAGE_STORAGE_KEY, conversationId, message, userId))
    .map(message => {
      const recalled = message?.recalled
        || message?.status === 'recalled'
        || hasStoredMessage(LOCAL_RECALLED_MESSAGE_STORAGE_KEY, conversationId, message, userId);
      return recalled ? withRecalledState(message) : message;
    })
    .filter(message => !shouldHideRecalledMessage(message));
}

function saveLocalMessage(message, userId) {
  if (!message?.conversationId) {
    return;
  }
  const messagesByConversation = readLocalMessages(userId);
  const messages = messagesByConversation[message.conversationId] || [];
  const keys = new Set(messageKeys(message));
  const index = messages.findIndex(item => messageKeys(item).some(key => keys.has(key)));
  if (index >= 0) {
    messages.splice(index, 1, message);
  } else {
    messages.push(message);
  }
  messagesByConversation[message.conversationId] = messages
    .sort((a, b) => Number(a.displayTimestamp || a.timestamp || 0) - Number(b.displayTimestamp || b.timestamp || 0))
    .slice(-200);
  writeLocalMessages(messagesByConversation, userId);
}

function removeLocalMessage(message, userId) {
  if (!message?.conversationId) {
    return;
  }
  const messagesByConversation = readLocalMessages(userId);
  const targetKeys = new Set(messageKeys(message));
  messagesByConversation[message.conversationId] = (messagesByConversation[message.conversationId] || [])
    .filter(item => !messageKeys(item).some(key => targetKeys.has(key)));
  writeLocalMessages(messagesByConversation, userId);
}

function mergeMessages(localMessages, remoteMessages, conversationId, userId) {
  const merged = [];
  [...(localMessages || []), ...(remoteMessages || [])].forEach(message => {
    const keys = messageKeys(message);
    const matchedIndex = merged.findIndex(item => {
      const currentKeys = messageKeys(item);
      return keys.some(key => currentKeys.includes(key));
    });
    if (matchedIndex >= 0) {
      merged.splice(matchedIndex, 1, preserveRecalledState(merged[matchedIndex], message));
    } else {
      merged.push(message);
    }
  });
  return applyLocalMessageState(conversationId, merged, userId)
    .sort((a, b) => Number(a.displayTimestamp || a.timestamp || 0) - Number(b.displayTimestamp || b.timestamp || 0));
}

function parsePhoneAccount(value) {
  const raw = String(value || '').trim();
  const defaultAreaCode = String(appConfig.openIMDefaultAreaCode || '+86').replace(/^\+?/, '+');
  const compact = raw.replace(/[\s-]/g, '');
  const match = compact.match(/^\+(\d{1,4})(\d{5,})$/);
  if (match) {
    return {
      areaCode: `+${match[1]}`,
      phoneNumber: match[2]
    };
  }
  return {
    areaCode: defaultAreaCode,
    phoneNumber: compact.replace(/\D/g, '')
  };
}

function normalizeContactKeyword(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  const compact = raw.replace(/[\s-]/g, '');
  if (!compact) {
    return '';
  }
  const normalizedPhone = compact.replace(/[-()]/g, '');
  return normalizedPhone.startsWith('+') ? normalizedPhone.slice(1) : normalizedPhone;
}

function isUserIdLike(value) {
  return /^\d{5,}$/.test(value || '');
}

function matchByKeywords(user, value, variants) {
  const normalizedValue = String(value || '').trim().toLowerCase();
  if (!normalizedValue) {
    return false;
  }
  const fields = [user?.userID, user?.phoneNumber, user?.account, user?.email, user?.nickname]
    .concat(variants || [])
    .filter(Boolean)
    .map(item => String(item).trim().toLowerCase());
  return fields.some(item => item === normalizedValue);
}

function chatUrl(path) {
  return `${String(appConfig.appServer || '').replace(/\/$/, '')}${path}`;
}

function normalizeApiError(responseData, fallbackMessage) {
  if (!responseData || typeof responseData.errCode === 'undefined' || Number(responseData.errCode) === 0) {
    return '';
  }
  return responseData.errMsg || responseData.errDlt || fallbackMessage;
}

function unwrapApiResponse(response, fallbackMessage) {
  const payload = response?.data ?? response;
  const error = normalizeApiError(payload, fallbackMessage);
  if (error) {
    throw new Error(error);
  }
  return payload?.data ?? payload;
}

function normalizeSdkError(error, fallbackMessage) {
  if (!error) {
    return fallbackMessage;
  }
  return error.errMsg || error.message || error.errDlt || fallbackMessage;
}

function isAuthenticationFailure(error) {
  const details = [error, error?.data, error?.error].filter(Boolean);
  const codes = details
    .flatMap(item => [item?.errCode, item?.code])
    .map(value => Number(value))
    .filter(Number.isFinite);
  const text = details
    .flatMap(item => [item?.errMsg, item?.errDlt, item?.message, typeof item === 'string' ? item : ''])
    .filter(Boolean)
    .join(' ');
  return codes.includes(1506)
    || /TokenKicked|kicked\s*offline|token\s*(?:is\s*)?(?:expired|invalid)/i.test(text);
}

function normalizeLoginResult(responseData, fallbackAccount) {
  const data = unwrapApiResponse(responseData, '登录失败');
  const userId = data.userID || data.userId || data.uid || fallbackAccount;
  return {
    userId,
    uid: userId,
    token: data.imToken || data.token || '',
    imToken: data.imToken || data.token || '',
    chatToken: data.chatToken || '',
    displayName: data.nickname || data.account || userId,
    raw: data
  };
}

function passwordForOpenIM(password) {
  const value = String(password || '');
  if (!appConfig.openIMHashPassword) {
    return value;
  }
  if (/^[a-f0-9]{32}$/i.test(value)) {
    return value.toLowerCase();
  }
  return SparkMD5.hash(value);
}

function getTitle(value, fallback) {
  return String(value || fallback || '');
}

function avatarText(title) {
  return String(title || '?').slice(0, 1).toUpperCase();
}

function parseJsonValue(value, fallback = null) {
  if (!value || typeof value !== 'string') {
    return fallback;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function normalizeMessageActionLimit(value) {
  return Math.min(
    MAX_MESSAGE_ACTION_LIMIT_MINUTES,
    Math.max(0, Math.floor(Number(value) || 0))
  );
}

function parseGroupExtension(value) {
  const parsed = parseJsonValue(value, null);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return parsed;
  }
  return value
    ? { [JC_GROUP_LEGACY_EXTENSION_KEY]: String(value) }
    : {};
}

function serializeGroupExtension(extension) {
  const keys = Object.keys(extension || {});
  if (keys.length === 1 && keys[0] === JC_GROUP_LEGACY_EXTENSION_KEY) {
    return String(extension[JC_GROUP_LEGACY_EXTENSION_KEY] || '');
  }
  return keys.length ? JSON.stringify(extension) : '';
}

function messageActionSettingsFromGroup(group) {
  const extension = parseGroupExtension(group?.ex || '');
  const settings = extension[JC_GROUP_MESSAGE_ACTION_SETTINGS_KEY];
  const configured = Boolean(settings && typeof settings === 'object' && !Array.isArray(settings));
  return {
    configured,
    recallLimitMinutes: configured ? normalizeMessageActionLimit(settings.recallLimitMinutes) : 0,
    editLimitMinutes: configured ? normalizeMessageActionLimit(settings.editLimitMinutes) : 0
  };
}

function normalizeUserProfile(user, fallbackId = '') {
  const userId = user?.userID || user?.userId || fallbackId || '';
  const displayName = user?.nickname || user?.account || user?.userName || userId;
  return {
    userId,
    uid: userId,
    displayName,
    name: displayName,
    account: user?.account || '',
    mobile: [user?.areaCode, user?.phoneNumber].filter(Boolean).join(' '),
    phoneNumber: user?.phoneNumber || '',
    areaCode: user?.areaCode || '',
    email: user?.email || '',
    portrait: toPortableObjectPath(user?.faceURL || user?.faceUrl || ''),
    address: '',
    raw: user
  };
}

function roleLevelToText(roleLevel) {
  if (Number(roleLevel) === GroupMemberRole.Owner) {
    return 'owner';
  }
  if (Number(roleLevel) === GroupMemberRole.Admin) {
    return 'admin';
  }
  return 'member';
}

function textFromMessage(message) {
  if (!message) {
    return '';
  }
  const extension = parseJsonValue(message.ex, null);
  if (typeof extension?.jcCaption === 'string' && extension.jcCaption.trim()) {
    return extension.jcCaption.trim();
  }
  if (message.textElem?.content) {
    return message.textElem.content;
  }
  if (message.atTextElem?.text) {
    return message.atTextElem.text;
  }
  if (message.quoteElem?.text) {
    return message.quoteElem.text;
  }
  if (message.advancedTextElem?.text) {
    return message.advancedTextElem.text;
  }
  const content = parseJsonValue(message.content, null);
  return content?.content || content?.text || (typeof content === 'string' ? content : '');
}

function attachmentFromMessage(message) {
  if (message?.contentType === MessageType.PictureMessage && message.pictureElem) {
    const picture = message.pictureElem.sourcePicture || message.pictureElem.bigPicture || message.pictureElem.snapshotPicture || {};
    return {
      type: 'image',
      name: picture.uuid || '图片',
      mime: picture.type || '',
      size: Number(picture.size || 0),
      url: toPortableObjectPath(picture.url || '')
    };
  }
  if (message?.contentType === MessageType.FileMessage && message.fileElem) {
    return {
      type: 'file',
      name: message.fileElem.fileName || '文件',
      mime: message.fileElem.fileType || '',
      size: Number(message.fileElem.fileSize || 0),
      url: toPortableObjectPath(message.fileElem.sourceUrl || '')
    };
  }
  return null;
}

function previewFromMessage(message) {
  if (!message) {
    return '';
  }
  if (Number(message.contentType) === MessageType.FriendAdded) {
    return '好友申请已通过，你们现在可以开始聊天';
  }
  if (Number(message.contentType) === MessageType.RevokeMessage) {
    return '[已撤回]';
  }
  const editPayload = messageEditPayload(message);
  if (editPayload) {
    return editPayload.text;
  }
  const text = textFromMessage(message).replace(/\s+/g, ' ').trim();
  if (text) {
    return text;
  }
  const attachment = attachmentFromMessage(message);
  if (attachment?.type === 'image') {
    return '[图片]';
  }
  if (attachment?.type === 'file') {
    return `[文件] ${attachment.name || ''}`.trim();
  }
  if (message.notificationElem?.detail) {
    return message.notificationElem.detail;
  }
  return '';
}

function recallInfoFromMessage(message) {
  if (Number(message?.contentType) !== MessageType.RevokeMessage) {
    return null;
  }
  const content = typeof message?.content === 'string'
    ? parseJsonValue(message.content, null)
    : message?.content;
  const detail = message?.notificationElem?.detail || content?.detail || '';
  return typeof detail === 'string' ? parseJsonValue(detail, null) : detail;
}

function conversationIdFor(type, target) {
  return `${type === 'group' || type === SessionType.Group ? 'group' : 'single'}-${target}`;
}

function conversationTypeFor(conversation) {
  const rawType = conversation?.raw?.conversationType ?? conversation?.conversationType;
  if (Number(rawType) === SessionType.Group || conversation?.type === 'group' || conversation?.type === 'channel') {
    return 'group';
  }
  return 'single';
}

function targetForConversation(conversation) {
  return conversation?.target
    || conversation?.raw?.groupID
    || conversation?.raw?.userID
    || String(conversation?.id || '').replace(/^(single|group)-/, '');
}

function sdkSessionTypeFor(conversation) {
  return conversationTypeFor(conversation) === 'group' ? SessionType.Group : SessionType.Single;
}

function messageConversationId(message, currentUserId = '') {
  const isGroup = Number(message?.sessionType) === SessionType.Group;
  if (isGroup) {
    return conversationIdFor('group', message.groupID);
  }
  const target = message.sendID === currentUserId ? message.recvID : message.sendID;
  return conversationIdFor('single', target);
}

function normalizeMessage(message, currentUserId = '', forcedConversationId = '') {
  const conversationId = forcedConversationId || messageConversationId(message, currentUserId);
  const editPayload = messageEditPayload(message);
  const isSystemMessage = Number(message?.contentType) === MessageType.FriendAdded;
  const recallInfo = recallInfoFromMessage(message);
  const sourceSendID = recallInfo?.sourceMessageSendID || message?.sendID || '';
  const direction = isSystemMessage ? 'system' : sourceSendID === currentUserId ? 'out' : 'in';
  const attachment = attachmentFromMessage(message);
  const timestamp = Number(message?.sendTime || message?.createTime || Date.now());
  const recalled = Number(message?.contentType) === MessageType.RevokeMessage;
  const status = recalled
    ? 'recalled'
    : Number(message?.status) === MessageStatus.Failed
      ? 'failed'
      : Number(message?.status) === MessageStatus.Sending
        ? 'sending'
        : 'sent';
  return {
    id: message?.clientMsgID || message?.serverMsgID || randomId('msg-'),
    messageUid: message?.serverMsgID || message?.clientMsgID || '',
    conversationId,
    conversationTitle: '',
    from: message?.sendID || '',
    senderName: message?.senderNickname || message?.sendID || '',
    senderPortrait: toPortableObjectPath(message?.senderFaceUrl || message?.senderFaceURL || ''),
    senderRole: '',
    direction,
    type: editPayload ? 'message_edit' : isSystemMessage ? 'system' : attachment?.type || 'text',
    text: editPayload?.text ?? previewFromMessage(message),
    attachment,
    status,
    deliveryStatus: status === 'failed' || recalled || isSystemMessage
      ? ''
      : message?.isRead && direction === 'out'
        ? 'read'
        : status === 'sending'
          ? 'sending'
          : 'delivered',
    timestamp,
    displayTimestamp: timestamp,
    serverTimestamp: timestamp,
    recalled,
    recallStatus: recalled ? '已撤回' : '',
    editedAt: Number(editPayload?.editedAt || 0),
    originalText: editPayload?.originalText || '',
    activityTimestamp: Number(editPayload?.editedAt || timestamp),
    editTargetIds: editPayload?.targetIds || [],
    recallSourceSendID: recalled ? sourceSendID : '',
    raw: message
  };
}

function normalizeConversation(conversation, currentUserId = '') {
  const isGroup = Number(conversation?.conversationType) === SessionType.Group;
  const type = isGroup ? 'group' : 'single';
  const target = isGroup ? conversation?.groupID : conversation?.userID;
  const latestMessage = typeof conversation?.latestMsg === 'string'
    ? parseJsonValue(conversation.latestMsg, null)
    : conversation?.latestMsg;
  const latestRecallInfo = recallInfoFromMessage(latestMessage);
  const latestRecalled = Number(latestMessage?.contentType) === MessageType.RevokeMessage;
  const latestSourceSendID = latestRecallInfo?.sourceMessageSendID || latestMessage?.sendID || '';
  const latestRecallDirection = latestRecalled
    ? latestSourceSendID === currentUserId ? 'out' : 'in'
    : '';
  const title = getTitle(conversation?.showName, target);
  return {
    id: conversationIdFor(type, target),
    type,
    target,
    title,
    subtitle: latestRecalled ? '' : previewFromMessage(latestMessage) || conversation?.draftText || '',
    unread: Number(conversation?.unreadCount || 0),
    portrait: toPortableObjectPath(conversation?.faceURL || ''),
    avatarText: avatarText(title),
    updatedAt: Number(conversation?.latestMsgSendTime || conversation?.draftTextTime || Date.now()),
    role: '',
    raw: {
      ...conversation,
      currentUserId,
      latestRecallDirection
    }
  };
}

function normalizeContact(friend, fullInfo = null) {
  const userId = friend?.userID || fullInfo?.userID || '';
  const remark = friend?.remark || '';
  const displayName = fullInfo?.nickname || friend?.nickname || userId;
  const title = getTitle(remark, displayName);
  const rawUser = normalizeUserProfile(fullInfo || friend, userId);
  return {
    id: userId,
    uid: userId,
    title,
    subtitle: rawUser.mobile || rawUser.email || rawUser.account || userId,
    portrait: toPortableObjectPath(rawUser.portrait || friend?.faceURL || ''),
    address: rawUser.address || '',
    avatarText: avatarText(title),
    alias: remark,
    rawFriend: friend,
    rawUser
  };
}

function normalizeFriendApplication(application) {
  const fromUserID = String(application?.fromUserID || '').trim();
  const toUserID = String(application?.toUserID || '').trim();
  const title = getTitle(application?.fromNickname, fromUserID);
  const createTime = Number(application?.createTime || 0);
  return {
    id: [fromUserID, toUserID, createTime || 'latest'].filter(Boolean).join('-'),
    fromUserID,
    toUserID,
    title,
    subtitle: fromUserID,
    portrait: toPortableObjectPath(application?.fromFaceURL || ''),
    avatarText: avatarText(title),
    reqMsg: application?.reqMsg || '',
    handleMsg: application?.handleMsg || '',
    handleResult: Number(application?.handleResult ?? ApplicationHandleResult.Unprocessed),
    createTime,
    raw: application
  };
}

async function imageSize(file) {
  if (!file || !file.type?.startsWith('image/')) {
    return { width: 0, height: 0 };
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise(resolve => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth || 0, height: image.naturalHeight || 0 });
      image.onerror = () => resolve({ width: 0, height: 0 });
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export class OpenIMClient {
  constructor() {
    this.listeners = new Map();
    this.sdk = null;
    this.sdkEventsBound = false;
    this.connected = false;
    this.currentUser = null;
    this.connectPromise = null;
    this.conversationList = [];
    this.contactList = [];
    this.authenticationInvalidated = false;
    this.groupReceiptAcknowledged = new Set();
  }

  getClientId() {
    let clientId = localStorage.getItem(appConfig.clientIdStorageKey);
    if (!clientId) {
      clientId = randomId();
      localStorage.setItem(appConfig.clientIdStorageKey, clientId);
    }
    return clientId;
  }

  restoreClientId(clientId) {
    if (clientId) {
      localStorage.setItem(appConfig.clientIdStorageKey, clientId);
    }
  }

  getSdkVersion() {
    return 'OpenIM WASM 3.8.3';
  }

  async getMessageActionSettings() {
    const response = await fetch('/business-api/message-settings.php', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.ok === false) {
      throw new Error(payload.message || `消息时限读取失败 (${response.status})`);
    }
    const settings = payload.settings || {};
    return {
      recallLimitMinutes: Math.max(0, Number(settings.recall_limit_minutes) || 0),
      editLimitMinutes: Math.max(0, Number(settings.edit_limit_minutes) || 0)
    };
  }

  async ensureSdk() {
    if (!this.sdk) {
      this.sdk = getSDK({
        coreWasmPath: appConfig.openIMWasmCorePath,
        sqlWasmPath: appConfig.openIMSqlWasmPath,
        debug: Boolean(appConfig.openIMSdkDebug)
      });
      this.bindSdkEvents();
    }
    return this.sdk;
  }

  operationHeaders(extraHeaders = {}) {
    const headers = {
      operationID: `jc-web-${Date.now()}-${Math.random().toString(16).slice(2)}`
    };
    return {
      ...headers,
      ...extraHeaders
    };
  }

  authHeaders() {
    return this.operationHeaders({
      token: this.currentUser?.chatToken || ''
    });
  }

  async groupReceiptRequest(payload) {
    if (!this.currentUser?.token) {
      const error = new Error('GROUP_RECEIPT_AUTH_REQUIRED');
      error.code = 'GROUP_RECEIPT_AUTH_REQUIRED';
      throw error;
    }
    const response = await fetch(JC_GROUP_MESSAGE_RECEIPT_ENDPOINT, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      cache: 'no-store',
      credentials: 'same-origin',
      body: JSON.stringify({
        ...payload,
        token: this.currentUser.token
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      const error = new Error('GROUP_RECEIPT_UNAVAILABLE');
      error.code = data.errorCode || 'GROUP_RECEIPT_UNAVAILABLE';
      throw error;
    }
    return data;
  }

  async connectManagedBot(targetUserID) {
    if (!this.currentUser?.token || !targetUserID) {
      return false;
    }
    const response = await fetch(JC_BOT_MANAGEMENT_ENDPOINT, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      cache: 'no-store',
      credentials: 'same-origin',
      body: JSON.stringify({
        action: 'connect_bot',
        token: this.currentUser.token,
        targetUserID
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      throw new Error(data.message || `机器人连接失败 (${response.status})`);
    }
    return Boolean(data.connected && data.managedBot);
  }

  async botManagementRequest(action, payload = {}) {
    // During H5/Capacitor cold start the authenticated shell can mount one tick
    // before the OpenIM client object is restored. The persisted session has
    // already been validated by the session store, so use it as a short-lived
    // fallback instead of hiding owner-only bot controls until another reload.
    const authToken = this.currentUser?.token || storedManagementToken();
    if (!authToken) {
      const error = new Error('登录状态已失效，请重新登录');
      error.code = 'AUTH_REQUIRED';
      throw error;
    }
    const response = await fetch(JC_BOT_MANAGEMENT_ENDPOINT, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      cache: 'no-store',
      credentials: 'same-origin',
      body: JSON.stringify({
        ...payload,
        action,
        token: authToken
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      const error = new Error(data.message || `机器人管理请求失败 (${response.status})`);
      error.code = data.errorCode || `HTTP_${response.status}`;
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async listManagedBots() {
    const data = await this.botManagementRequest('list');
    return {
      botMother: data.botMother || null,
      bots: Array.isArray(data.bots) ? data.bots : []
    };
  }

  async createManagedBot(displayName, username) {
    const data = await this.botManagementRequest('create', { displayName, username });
    return data.bot || null;
  }

  async checkManagedBotUsername(username) {
    const data = await this.botManagementRequest('check_username', { username });
    return {
      username: String(data.username || username),
      available: Boolean(data.available)
    };
  }

  async rotateManagedBotToken(username) {
    return this.botManagementRequest('rotate_token', { username });
  }

  async revokeManagedBotToken(username) {
    const data = await this.botManagementRequest('revoke_token', { username });
    return data.bot || null;
  }

  async getManagedBotSettings(targetUserID) {
    const data = await this.botManagementRequest('bot_settings', { targetUserID });
    return data.details || null;
  }

  async saveManagedBotPreferences(targetUserID, preferences) {
    const data = await this.botManagementRequest('save_bot_preferences', {
      targetUserID,
      requireJoinApproval: Boolean(preferences?.requireJoinApproval),
      externalEnabled: Boolean(preferences?.externalEnabled)
    });
    return data.preferences || null;
  }

  async saveManagedBotGroupSetting(targetUserID, groupID, receiveEnabled) {
    const data = await this.botManagementRequest('save_bot_group_setting', {
      targetUserID,
      groupID,
      receiveEnabled: Boolean(receiveEnabled)
    });
    return data.group || null;
  }

  async saveManagedBotAutoReplies(targetUserID, rules) {
    const data = await this.botManagementRequest('save_bot_auto_replies', {
      targetUserID,
      rules: Array.isArray(rules) ? rules : []
    });
    return Array.isArray(data.autoReplies) ? data.autoReplies : [];
  }

  async respondManagedBotJoinRequest(targetUserID, requestID, approve) {
    const data = await this.botManagementRequest('respond_bot_join_request', {
      targetUserID,
      requestID,
      approve: Boolean(approve)
    });
    return data.request || null;
  }

  async resolveManagedBot(username) {
    const value = String(username || '').trim();
    if (!this.currentUser?.token || !value) {
      return null;
    }
    const response = await fetch(JC_BOT_MANAGEMENT_ENDPOINT, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      cache: 'no-store',
      credentials: 'same-origin',
      body: JSON.stringify({
        action: 'resolve_bot',
        token: this.currentUser.token,
        username: value
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      throw new Error(data.message || `机器人查询失败 (${response.status})`);
    }
    if (!data.bot?.botUserID) {
      return null;
    }
    return normalizeUserProfile({
      userID: data.bot.botUserID,
      nickname: data.bot.displayName,
      account: data.bot.username
    }, data.bot.botUserID);
  }

  async getPlatformSettings() {
    if (!this.currentUser?.token) {
      throw new Error('登录状态已失效，请重新登录');
    }
    const response = await fetch(JC_BOT_MANAGEMENT_ENDPOINT, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      cache: 'no-store',
      credentials: 'same-origin',
      body: JSON.stringify({
        action: 'platform_settings',
        token: this.currentUser.token
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      throw new Error(data.message || `系统设置读取失败 (${response.status})`);
    }
    return {
      groupMaxMembers: Math.max(2, Number(data.settings?.group_max_members) || 500),
      botMaxPerOwner: Math.max(1, Number(data.settings?.bot_max_per_owner) || 20)
    };
  }

  async reportGroupMessageReceipts(conversation, messages, status) {
    const groupMessages = (Array.isArray(messages) ? messages : [messages])
      .filter(Boolean)
      .map(message => ({
        message,
        identity: groupReceiptMessageIdentity(conversation, message)
      }))
      .filter(({ message, identity }) => {
        if (!identity || !['sent', 'delivered', 'read'].includes(status)) {
          return false;
        }
        return status === 'sent' ? message.direction === 'out' : message.direction === 'in';
      });
    if (!groupMessages.length) {
      return 0;
    }
    const pending = groupMessages.filter(({ identity }) => {
      const key = `${status}:${identity.groupID}:${identity.clientMsgID || identity.serverMsgID}`;
      if (this.groupReceiptAcknowledged.has(key)) {
        return false;
      }
      this.groupReceiptAcknowledged.add(key);
      return true;
    });
    if (!pending.length) {
      return 0;
    }
    try {
      await this.groupReceiptRequest({
        action: 'ack',
        receipts: pending.map(({ identity }) => ({ status, message: identity }))
      });
      return pending.length;
    } catch (error) {
      pending.forEach(({ identity }) => {
        this.groupReceiptAcknowledged.delete(`${status}:${identity.groupID}:${identity.clientMsgID || identity.serverMsgID}`);
      });
      throw error;
    }
  }

  async loginWithPassword(mobile, password) {
    const deviceId = this.getClientId();
    const phone = parsePhoneAccount(mobile);
    const response = await axios.post(chatUrl('/account/login'), {
      areaCode: phone.areaCode,
      phoneNumber: phone.phoneNumber,
      password: passwordForOpenIM(password),
      platform: appConfig.platform,
      deviceID: deviceId
    }, {
      withCredentials: false,
      headers: this.operationHeaders()
    });
    return normalizeLoginResult(response, mobile);
  }

  async requestAuthCode(mobile, usedFor = 3) {
    const phone = parsePhoneAccount(mobile);
    const response = await axios.post(chatUrl('/account/code/send'), {
      usedFor,
      platform: appConfig.platform,
      deviceID: this.getClientId(),
      areaCode: phone.areaCode,
      phoneNumber: phone.phoneNumber
    }, {
      withCredentials: false,
      headers: this.operationHeaders()
    });
    return unwrapApiResponse(response, '验证码发送失败');
  }

  async loginWithCode(mobile, code) {
    const phone = parsePhoneAccount(mobile);
    const response = await axios.post(chatUrl('/account/login'), {
      areaCode: phone.areaCode,
      phoneNumber: phone.phoneNumber,
      verifyCode: code,
      platform: appConfig.platform,
      deviceID: this.getClientId()
    }, {
      withCredentials: false,
      headers: this.operationHeaders()
    });
    return normalizeLoginResult(response, mobile);
  }

  async registerWithPhone(mobile, code) {
    const phone = parsePhoneAccount(mobile);
    const response = await axios.post(chatUrl('/account/register'), {
      verifyCode: code,
      platform: appConfig.platform,
      autoLogin: false,
      deviceID: this.getClientId(),
      user: {
        nickname: phone.phoneNumber,
        areaCode: phone.areaCode,
        phoneNumber: phone.phoneNumber,
        password: ''
      }
    }, {
      withCredentials: false,
      headers: this.operationHeaders()
    });
    const data = unwrapApiResponse(response, '注册失败');
    return {
      userId: data.userID || data.userId || '',
      resetCode: code,
      raw: data
    };
  }

  async connect(userId, token, context = {}) {
    if (!userId || !token) {
      return;
    }
    const connectedUserId = this.currentUser?.userId || '';
    if (connectedUserId && connectedUserId !== userId) {
      this.groupReceiptAcknowledged.clear();
    }
    this.currentUser = {
      userId,
      token,
      chatToken: context.chatToken || this.currentUser?.chatToken || '',
      displayName: context.displayName || userId,
      portrait: toPortableObjectPath(context.portrait || this.currentUser?.portrait || '')
    };
    if (this.connected && connectedUserId === userId) {
      return;
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }
    const sdk = await this.ensureSdk();
    this.authenticationInvalidated = false;
    this.emit('connectionStatusChanged', { connected: false, statusText: '连接中' });
    this.connectPromise = (async () => {
      try {
        unwrapSdkResponse(await sdk.login({
          userID: userId,
          token,
          platformID: appConfig.platform,
          apiAddr: appConfig.openIMApiServer,
          wsAddr: appConfig.wsUrl,
          logLevel: appConfig.openIMLogLevel,
          isLogStandardOutput: Boolean(appConfig.openIMSdkDebug)
        }), '连接失败');
        this.connected = true;
        this.emit('connectionStatusChanged', { connected: true, statusText: '已连接' });
        await Promise.allSettled([
          this.refreshConversationList(),
          this.refreshContactList()
        ]);
      } catch (error) {
        this.connected = false;
        if (isAuthenticationFailure(error)) {
          this.notifyAuthenticationInvalid();
          throw error;
        }
        this.emit('connectionStatusChanged', {
          connected: false,
          statusText: normalizeSdkError(error, '连接失败')
        });
        throw error;
      } finally {
        this.connectPromise = null;
      }
    })();
    return this.connectPromise;
  }

  async disconnect() {
    if (this.sdk && this.connected) {
      await this.sdk.logout().catch(() => {});
    }
    this.connected = false;
    this.currentUser = null;
    this.conversationList = [];
    this.contactList = [];
    this.groupReceiptAcknowledged.clear();
    this.emit('connectionStatusChanged', { connected: false, statusText: '未连接' });
  }

  notifyAuthenticationInvalid() {
    if (this.authenticationInvalidated) {
      return;
    }
    this.authenticationInvalidated = true;
    this.connected = false;
    this.emit('connectionStatusChanged', { connected: false, statusText: '登录状态已失效' });
    this.emit('authenticationInvalid', { errorKey: 'auth.sessionExpired' });
  }

  refreshAfterServerSync() {
    if (!this.currentUser?.userId || !this.connected) {
      return;
    }
    Promise.allSettled([
      this.refreshConversationList(),
      this.refreshContactList()
    ]).finally(() => {
      if (this.connected) {
        this.emit('connectionStatusChanged', { connected: true, statusText: '已连接' });
      }
    });
  }

  bindSdkEvents() {
    if (!this.sdk || this.sdkEventsBound) {
      return;
    }
    this.sdkEventsBound = true;
    this.sdk.on(CbEvents.OnConnecting, () => {
      this.emit('connectionStatusChanged', { connected: false, statusText: '连接中' });
    });
    this.sdk.on(CbEvents.OnConnectSuccess, () => {
      this.connected = true;
      this.emit('connectionStatusChanged', { connected: true, statusText: '已连接' });
    });
    this.sdk.on(CbEvents.OnConnectFailed, event => {
      this.connected = false;
      if (isAuthenticationFailure(event)) {
        this.notifyAuthenticationInvalid();
        return;
      }
      this.emit('connectionStatusChanged', {
        connected: false,
        statusText: normalizeSdkError(event, '连接失败')
      });
    });
    this.sdk.on(CbEvents.OnUserTokenExpired, () => {
      this.notifyAuthenticationInvalid();
    });
    this.sdk.on(CbEvents.OnUserTokenInvalid, () => {
      this.notifyAuthenticationInvalid();
    });
    this.sdk.on(CbEvents.OnKickedOffline, () => {
      this.notifyAuthenticationInvalid();
    });
    this.sdk.on(CbEvents.OnSyncServerStart, () => {
      this.emit('connectionStatusChanged', { connected: this.connected, statusText: '同步中' });
    });
    this.sdk.on(CbEvents.OnSyncServerFinish, () => {
      this.refreshAfterServerSync();
    });
    this.sdk.on(CbEvents.OnSyncServerFailed, () => {
      this.emit('connectionStatusChanged', { connected: this.connected, statusText: '同步失败' });
    });
    const handleMessages = event => {
      const data = event?.data ?? event;
      const messages = Array.isArray(data) ? data : [data].filter(Boolean);
      const normalizedMessages = messages.map(message => normalizeMessage(message, this.currentUser?.userId || ''));
      normalizedMessages.forEach(message => {
        saveLocalMessage(message, this.currentUser?.userId || '');
        if (message.type === 'message_edit') {
          const storedMessages = readLocalMessages(this.currentUser?.userId || '')[message.conversationId] || [];
          const editedMessage = applyMessageEditEvents(storedMessages).find(item => {
            const itemKeys = new Set(messageKeys(item).map(value => String(value)));
            return message.editTargetIds.some(id => itemKeys.has(String(id)));
          });
          if (editedMessage) {
            saveLocalMessage(editedMessage, this.currentUser?.userId || '');
            this.emit('messageEdited', editedMessage);
          }
          return;
        }
        this.emit('messageReceived', message);
      });
      this.reportGroupMessageReceipts(null, normalizedMessages, 'delivered').catch(() => {});
      this.refreshConversationList().catch(() => {});
    };
    this.sdk.on(CbEvents.OnRecvNewMessage, handleMessages);
    this.sdk.on(CbEvents.OnRecvNewMessages, handleMessages);
    this.sdk.on(CbEvents.OnRecvOfflineNewMessages, handleMessages);
    this.sdk.on(CbEvents.OnRecvOnlineOnlyMessages, handleMessages);
    const markOutgoingMessagesRead = messageIds => {
      const ids = new Set((messageIds || []).filter(Boolean));
      if (!ids.size) return;
      const userId = this.currentUser?.userId || '';
      const messagesByConversation = readLocalMessages(userId);
      Object.entries(messagesByConversation).forEach(([conversationId, messages]) => {
        messagesByConversation[conversationId] = messages.map(message => {
          const keys = [message.id, message.messageUid, message.raw?.clientMsgID, message.raw?.serverMsgID];
          if (message.direction !== 'out' || !keys.some(key => ids.has(key))) return message;
          const updated = { ...message, deliveryStatus: 'read' };
          this.emit('messageStatusChanged', updated);
          return updated;
        });
      });
      writeLocalMessages(messagesByConversation, userId);
    };
    this.sdk.on(CbEvents.OnRecvC2CReadReceipt, event => {
      const receipts = event?.data ?? event;
      markOutgoingMessagesRead((Array.isArray(receipts) ? receipts : [receipts])
        .flatMap(receipt => receipt?.msgIDList || []));
    });
    this.sdk.on(CbEvents.OnRecvGroupReadReceipt, event => {
      const receipt = event?.data ?? event;
      markOutgoingMessagesRead((receipt?.groupMessageReadInfo || [])
        .filter(item => Number(item?.hasReadCount || 0) > 0)
        .map(item => item?.clientMsgID));
    });
    const handleMessageRecalled = event => {
      const payload = event?.data ?? event;
      const data = typeof payload === 'string' ? parseJsonValue(payload, {}) : payload;
      const clientMsgID = data?.clientMsgID;
      if (!clientMsgID) {
        return;
      }
      const userId = this.currentUser?.userId || '';
      const messagesByConversation = readLocalMessages(userId);
      const recalled = applyRecallEvent(messagesByConversation, clientMsgID);
      recalled.matches.forEach(({ conversationId, message, action }) => {
        storeMessageKeys(
          action === 'hidden' ? LOCAL_HIDDEN_MESSAGE_STORAGE_KEY : LOCAL_RECALLED_MESSAGE_STORAGE_KEY,
          conversationId,
          message,
          userId
        );
      });
      writeLocalMessages(recalled.messagesByConversation, userId);
      recalled.matches.forEach(({ message, action }) => {
        this.emit(action === 'hidden' ? 'messageHidden' : 'messageStatusChanged', message);
      });
      this.refreshConversationList().catch(() => {});
    };
    this.sdk.on(CbEvents.OnRecvMessageRevoked, handleMessageRecalled);
    this.sdk.on(CbEvents.OnNewRecvMessageRevoked, handleMessageRecalled);
    const refreshConversations = event => {
      const data = event?.data ?? event;
      if (Array.isArray(data) && data.length) {
        this.conversationList = data.map(item => normalizeConversation(item, this.currentUser?.userId || ''));
        this.emit('conversationsChanged', this.conversationList);
        return;
      }
      this.refreshConversationList().catch(() => {});
    };
    this.sdk.on(CbEvents.OnNewConversation, refreshConversations);
    this.sdk.on(CbEvents.OnConversationChanged, refreshConversations);
    const refreshContacts = () => this.refreshContactList().catch(() => {});
    const refreshFriendApplications = event => {
      this.emit('friendApplicationsChanged', event?.data ?? event);
    };
    this.sdk.on(CbEvents.OnFriendAdded, refreshContacts);
    this.sdk.on(CbEvents.OnFriendDeleted, refreshContacts);
    this.sdk.on(CbEvents.OnFriendInfoChanged, refreshContacts);
    this.sdk.on(CbEvents.OnFriendApplicationAdded, refreshFriendApplications);
    this.sdk.on(CbEvents.OnFriendApplicationDeleted, refreshFriendApplications);
    this.sdk.on(CbEvents.OnFriendApplicationRejected, refreshFriendApplications);
    this.sdk.on(CbEvents.OnFriendApplicationAccepted, event => {
      refreshFriendApplications(event);
      refreshContacts();
    });
    this.sdk.on(CbEvents.OnJoinedGroupAdded, () => this.refreshConversationList().catch(() => {}));
    this.sdk.on(CbEvents.OnJoinedGroupDeleted, () => this.refreshConversationList().catch(() => {}));
    this.sdk.on(CbEvents.OnGroupInfoChanged, event => {
      this.emit('groupInfoChanged', event?.data ?? event);
      this.refreshConversationList().catch(() => {});
    });
  }

  async ensureConnected() {
    if (!this.currentUser?.userId) {
      throw new Error('请先登录');
    }
    if (!this.connected && this.connectPromise) {
      await this.connectPromise;
    }
    if (!this.connected) {
      throw new Error('连接已失效，请重新登录');
    }
    return this.ensureSdk();
  }

  getConversationList() {
    return this.conversationList;
  }

  async refreshConversationList() {
    if (!this.currentUser?.userId) {
      return [];
    }
    const conversations = await this.fetchRemoteConversations(50);
    this.conversationList = conversations;
    this.emit('conversationsChanged', conversations);
    return conversations;
  }

  async fetchRemoteConversations(limit = 50) {
    const sdk = await this.ensureConnected();
    const response = await sdk.getConversationListSplit({ offset: 0, count: limit });
    const conversations = unwrapSdkResponse(response, '会话同步失败') || [];
    return conversations.map(item => normalizeConversation(item, this.currentUser?.userId || ''));
  }

  getMessages(conversation) {
    if (!conversation?.id) {
      return [];
    }
    const userId = this.currentUser?.userId || '';
    return applyLocalMessageState(conversation.id, readLocalMessages(userId)[conversation.id] || [], userId);
  }

  async getSdkConversationID(conversation) {
    const sdk = await this.ensureConnected();
    if (conversation?.raw?.conversationID) {
      return conversation.raw.conversationID;
    }
    const target = targetForConversation(conversation);
    const sessionType = sdkSessionTypeFor(conversation);
    const response = await sdk.getConversationIDBySessionType({
      sourceID: target,
      sessionType
    });
    return unwrapSdkResponse(response, '会话ID获取失败');
  }

  async fetchRemoteMessages(conversation, limit = 50, options = {}) {
    if (!conversation?.id) {
      return [];
    }
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    const response = await sdk.getAdvancedHistoryMessageList({
      count: limit,
      viewType: ViewType.History,
      startClientMsgID: options.startClientMsgID || '',
      conversationID
    });
    const data = unwrapSdkResponse(response, '消息同步失败') || {};
    const remoteMessages = (data.messageList || [])
      .map(message => normalizeMessage(message, this.currentUser?.userId || '', conversation.id));
    const userId = this.currentUser?.userId || '';
    const messagesByConversation = readLocalMessages(userId);
    const merged = mergeMessages(messagesByConversation[conversation.id] || [], remoteMessages, conversation.id, userId);
    messagesByConversation[conversation.id] = merged;
    writeLocalMessages(messagesByConversation, userId);
    if (conversationTypeFor(conversation) === 'group') {
      await this.reportGroupMessageReceipts(conversation, remoteMessages, 'delivered').catch(() => {});
    }
    if (options.markRead) {
      await sdk.markConversationMessageAsRead(conversationID).catch(() => {});
      if (conversationTypeFor(conversation) === 'group') {
        await this.reportGroupMessageReceipts(conversation, merged, 'read').catch(() => {});
      }
    }
    return merged;
  }

  async searchConversationMessages(conversation, keyword, limit = 200) {
    const value = String(keyword || '').trim();
    if (!conversation?.id || !value) {
      return [];
    }
    const messages = await this.fetchAllConversationMessages(conversation);
    return messages
      .filter(message => searchableMessageText(message).includes(value.toLocaleLowerCase()))
      .sort((left, right) => Number(right.displayTimestamp || right.timestamp || 0) - Number(left.displayTimestamp || left.timestamp || 0))
      .slice(0, Math.min(500, Math.max(1, Number(limit) || 200)));
  }

  async fetchAllConversationMessages(conversation, maxMessages = Number.POSITIVE_INFINITY) {
    if (!conversation?.id) {
      return [];
    }
    const requestedMax = Number(maxMessages);
    const resolvedMax = Number.isFinite(requestedMax)
      ? Math.max(1, Math.trunc(requestedMax))
      : Number.POSITIVE_INFINITY;
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    const pageSize = 100;
    const rawMessages = [];
    const seen = new Set();
    let startClientMsgID = '';
    let isEnd = false;
    while (!isEnd && rawMessages.length < resolvedMax) {
      const response = await sdk.getAdvancedHistoryMessageList({
        count: Math.min(pageSize, resolvedMax - rawMessages.length),
        viewType: ViewType.History,
        startClientMsgID,
        conversationID
      });
      const data = unwrapSdkResponse(response, '完整聊天记录加载失败') || {};
      const batch = Array.isArray(data.messageList) ? data.messageList : [];
      let added = 0;
      batch.forEach(message => {
        const key = String(message?.serverMsgID || message?.clientMsgID || `${message?.seq || ''}:${message?.sendTime || message?.createTime || ''}`);
        if (!key || seen.has(key)) return;
        seen.add(key);
        rawMessages.push(message);
        added += 1;
      });
      isEnd = Boolean(data.isEnd) || !batch.length || !added;
      const nextStart = String(batch.at(-1)?.clientMsgID || '');
      if (!nextStart || nextStart === startClientMsgID) {
        isEnd = true;
      } else {
        startClientMsgID = nextStart;
      }
    }
    const userId = this.currentUser?.userId || '';
    const normalized = rawMessages.map(message => normalizeMessage(message, userId, conversation.id));
    const cachedMessages = readLocalMessages(userId)[conversation.id] || [];
    return mergeMessages(cachedMessages, normalized, conversation.id, userId);
  }

  async setConversationMuted(conversation, muted) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    const recvMsgOpt = muted ? MessageReceiveOptType.NotNotify : MessageReceiveOptType.Normal;
    unwrapSdkResponse(await sdk.setConversation({ conversationID, recvMsgOpt }), '通知设置失败');
    return recvMsgOpt;
  }

  async setConversationPinned(conversation, pinned) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    unwrapSdkResponse(await sdk.setConversation({ conversationID, isPinned: Boolean(pinned) }), '特别关注设置失败');
    return Boolean(pinned);
  }

  async setConversationDestruct(conversation, seconds) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    const msgDestructTime = Math.max(0, Math.trunc(Number(seconds) || 0));
    unwrapSdkResponse(await sdk.setConversation({
      conversationID,
      isMsgDestruct: msgDestructTime > 0,
      msgDestructTime
    }), '限时消息设置失败');
    return msgDestructTime;
  }

  async hideConversation(conversation) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    unwrapSdkResponse(await sdk.hideConversation(conversationID), '关闭聊天失败');
  }

  async clearConversationMessages(conversation) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    unwrapSdkResponse(await sdk.clearConversationAndDeleteAllMsg(conversationID), '清空聊天失败');
    clearLocalConversationMessages(conversation.id, this.currentUser?.userId || '');
  }

  async deleteConversation(conversation) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    unwrapSdkResponse(await sdk.deleteConversationAndDeleteAllMsg(conversationID), '删除聊天失败');
    clearLocalConversationMessages(conversation.id, this.currentUser?.userId || '');
  }

  hideMessages(conversation, messages) {
    if (!conversation?.id) {
      return;
    }
    const userId = this.currentUser?.userId || '';
    storeMessageKeys(LOCAL_HIDDEN_MESSAGE_STORAGE_KEY, conversation.id, messages, userId);
  }

  markMessagesRecalled(conversation, messages) {
    if (!conversation?.id) {
      return;
    }
    const userId = this.currentUser?.userId || '';
    storeMessageKeys(LOCAL_RECALLED_MESSAGE_STORAGE_KEY, conversation.id, messages, userId);
  }

  async recallMessage(conversation, message) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    const clientMsgID = message?.raw?.clientMsgID || message?.id;
    if (!clientMsgID) {
      throw new Error('缺少可撤回的消息ID');
    }
    unwrapSdkResponse(await sdk.revokeMessage({ conversationID, clientMsgID }), '撤回失败');
    this.markMessagesRecalled(conversation, [message]);
    const recalledMessage = withRecalledState(message);
    saveLocalMessage(recalledMessage, this.currentUser?.userId || '');
    this.emit('messageStatusChanged', recalledMessage);
    return true;
  }

  async editMessage(conversation, message, text) {
    if (message?.direction !== 'out') {
      throw new Error('只能编辑自己发送的消息');
    }
    const targetClientMsgID = message?.raw?.clientMsgID || message?.id || '';
    const targetServerMsgID = message?.raw?.serverMsgID || message?.messageUid || '';
    if (!targetClientMsgID && !targetServerMsgID) {
      throw new Error('缺少可编辑的消息ID');
    }
    const sdk = await this.ensureConnected();
    const editedAt = Date.now();
    const originalText = message?.originalText || message?.text || '';
    const payload = {
      type: JC_MESSAGE_EDIT_TYPE,
      targetClientMsgID,
      targetServerMsgID,
      originalText,
      text,
      editedAt
    };
    const created = unwrapSdkMessage(await sdk.createCustomMessage({
      data: JSON.stringify(payload),
      extension: JSON.stringify({ type: JC_MESSAGE_EDIT_TYPE, version: 1 }),
      description: JC_MESSAGE_EDIT_DESCRIPTION
    }), '编辑消息创建失败');
    const recvID = conversationTypeFor(conversation) === 'single' ? targetForConversation(conversation) : '';
    const groupID = conversationTypeFor(conversation) === 'group' ? targetForConversation(conversation) : '';
    const currentUserId = this.currentUser?.userId || '';
    const pendingEditEvent = {
      ...created,
      sendID: created.sendID || currentUserId,
      recvID: created.recvID || recvID,
      groupID: created.groupID || groupID,
      sessionType: created.sessionType || sdkSessionTypeFor(conversation),
      contentType: created.contentType || MessageType.CustomMessage,
      createTime: Number(created.createTime || editedAt),
      sendTime: Number(created.sendTime || editedAt)
    };
    const editEvent = normalizeMessage(pendingEditEvent, currentUserId, conversation.id);
    saveLocalMessage(editEvent, this.currentUser?.userId || '');
    const editedMessage = {
      ...message,
      originalText,
      text,
      editedAt,
      activityTimestamp: editedAt,
      editEventId: editEvent.messageUid || editEvent.id || ''
    };
    saveLocalMessage(editedMessage, this.currentUser?.userId || '');
    Promise.resolve()
      .then(() => sdk.sendMessage({ recvID, groupID, message: created }))
      .then(response => {
        const sent = unwrapSdkResponse(response, '编辑消息发送失败');
        saveLocalMessage(normalizeMessage(sent, currentUserId, conversation.id), currentUserId);
        this.refreshConversationList().catch(() => {});
      })
      .catch(error => {
        removeLocalMessage(editEvent, currentUserId);
        saveLocalMessage(message, currentUserId);
        this.emit('messageEditFailed', {
          conversationId: conversation.id,
          originalMessage: message,
          editedMessage,
          error: normalizeSdkError(error, '编辑消息发送失败')
        });
      });
    return editedMessage;
  }

  async getMessageDetail(conversation, message) {
    const sdk = await this.ensureConnected();
    const conversationID = await this.getSdkConversationID(conversation);
    const clientMsgID = message?.raw?.clientMsgID || message?.id || '';
    const detail = {
      messageUid: message?.messageUid || clientMsgID,
      conversationType: conversationTypeFor(conversation),
      sentAt: Number(message?.displayTimestamp || message?.timestamp || 0),
      editedAt: 0,
      recalled: Boolean(message?.recalled),
      localOnly: false,
      sender: {
        uid: message?.from || '',
        name: message?.senderName || message?.from || ''
      }
    };
    if (conversationTypeFor(conversation) === 'group') {
      const identity = groupReceiptMessageIdentity(conversation, message);
      if (!identity) {
        return { ...detail, localOnly: true };
      }
      if (message?.direction !== 'out' || identity.senderID !== this.currentUser?.userId) {
        const error = new Error('GROUP_RECEIPT_SENDER_ONLY');
        error.code = 'GROUP_RECEIPT_SENDER_ONLY';
        throw error;
      }
      const [members, response] = await Promise.all([
        this.getGroupMembers(this.currentUser.userId, identity.groupID),
        this.groupReceiptRequest({ action: 'detail', message: identity })
      ]);
      return {
        ...detail,
        messageUid: message?.messageUid || identity.serverMsgID || identity.clientMsgID,
        sentAt: identity.sentAt || detail.sentAt,
        localOnly: false,
        group: classifyGroupMessageReceipts({
          members,
          receipts: response.receipts || [],
          senderID: identity.senderID,
          sentAt: identity.sentAt
        })
      };
    }
    if (!clientMsgID) {
      return { ...detail, localOnly: true };
    }
    const response = await sdk.findMessageList([{ conversationID, clientMsgIDList: [clientMsgID] }]).catch(() => null);
    const data = response ? unwrapSdkResponse(response, '消息详情加载失败') : null;
    const found = data?.findResultItems?.[0]?.messageList?.[0] || data?.searchResultItems?.[0]?.messageList?.[0];
    return found ? {
      ...detail,
      raw: found,
      sentAt: Number(found.sendTime || found.createTime || detail.sentAt)
    } : detail;
  }

  async sendTextMessage(conversation, text, initialMessage = null) {
    const sdk = await this.ensureConnected();
    const message = initialMessage || {
      id: randomId(),
      conversationId: conversation.id,
      from: this.currentUser?.userId || 'me',
      direction: 'out',
      type: 'text',
      text,
      status: 'sending',
      deliveryStatus: 'sending',
      timestamp: Date.now(),
      displayTimestamp: Date.now()
    };
    saveLocalMessage(message, this.currentUser?.userId || '');
    if (!initialMessage) {
      this.emit('messageSent', message);
    }
    try {
      // The SDK wrapper applies `data[0]` before JSON decoding. Some persisted WASM
      // states return a JSON-encoded array, so the wrapper truncates it to "[".
      // Invoke the WASM factory directly and normalize both array representations.
      const created = await createTextMessageFromWasm(text);
      const sent = unwrapSdkResponse(await sdk.sendMessage({
        recvID: conversationTypeFor(conversation) === 'single' ? targetForConversation(conversation) : '',
        groupID: conversationTypeFor(conversation) === 'group' ? targetForConversation(conversation) : '',
        message: created
      }), '发送失败');
      const normalized = {
        ...normalizeMessage(sent, this.currentUser?.userId || '', conversation.id),
        id: message.id,
        text,
        status: 'sent',
        deliveryStatus: 'delivered'
      };
      saveLocalMessage(normalized, this.currentUser?.userId || '');
      this.emit('messageStatusChanged', normalized);
      this.reportGroupMessageReceipts(conversation, normalized, 'sent').catch(() => {});
      this.refreshConversationList().catch(() => {});
      return normalized;
    } catch (error) {
      const failed = {
        ...message,
        status: 'failed',
        deliveryStatus: '',
        error: normalizeSdkError(error, '发送失败')
      };
      saveLocalMessage(failed, this.currentUser?.userId || '');
      this.emit('messageStatusChanged', failed);
      return failed;
    }
  }

  async sendAttachmentMessage(conversation, { type, file, text = '' }, initialMessage = null) {
    const sdk = await this.ensureConnected();
    const message = initialMessage || {
      id: randomId(),
      conversationId: conversation.id,
      from: this.currentUser?.userId || 'me',
      direction: 'out',
      type,
      text,
      attachment: {
        type,
        name: file?.name || (type === 'image' ? '图片' : '文件'),
        mime: file?.type || '',
        size: file?.size || 0,
        url: type === 'image' && file ? URL.createObjectURL(file) : ''
      },
      status: 'sending',
      deliveryStatus: 'sending',
      timestamp: Date.now(),
      displayTimestamp: Date.now()
    };
    saveLocalMessage(message, this.currentUser?.userId || '');
    if (!initialMessage) {
      this.emit('messageSent', message);
    }
    try {
      const uuid = randomId('file-');
      let created;
      if (type === 'image') {
        const size = await imageSize(file);
        const picture = {
          uuid,
          type: file?.type || 'image/*',
          size: Number(file?.size || 0),
          width: size.width,
          height: size.height,
          url: ''
        };
        created = unwrapSdkMessage(await sdk.createImageMessageByFile({
          sourcePicture: { ...picture },
          bigPicture: { ...picture },
          snapshotPicture: { ...picture },
          sourcePath: '',
          file
        }), '图片消息创建失败');
        if (text.trim()) {
          created.ex = JSON.stringify({ jcCaption: text.trim() });
        }
      } else {
        created = unwrapSdkMessage(await sdk.createFileMessageByFile({
          filePath: '',
          fileName: file?.name || '文件',
          uuid,
          sourceUrl: '',
          fileSize: Number(file?.size || 0),
          fileType: file?.type || '',
          file
        }), '文件消息创建失败');
      }
      const sent = unwrapSdkResponse(await sdk.sendMessage({
        recvID: conversationTypeFor(conversation) === 'single' ? targetForConversation(conversation) : '',
        groupID: conversationTypeFor(conversation) === 'group' ? targetForConversation(conversation) : '',
        message: created
      }), '发送失败');
      const normalized = {
        ...normalizeMessage(sent, this.currentUser?.userId || '', conversation.id),
        id: message.id,
        text,
        status: 'sent',
        deliveryStatus: 'delivered'
      };
      saveLocalMessage(normalized, this.currentUser?.userId || '');
      this.emit('messageStatusChanged', normalized);
      this.reportGroupMessageReceipts(conversation, normalized, 'sent').catch(() => {});
      this.refreshConversationList().catch(() => {});
      return normalized;
    } catch (error) {
      const failed = {
        ...message,
        status: 'failed',
        deliveryStatus: '',
        error: normalizeSdkError(error, '发送失败')
      };
      saveLocalMessage(failed, this.currentUser?.userId || '');
      this.emit('messageStatusChanged', failed);
      return failed;
    }
  }

  getContactList() {
    return this.contactList;
  }

  async getServerContactList() {
    const sdk = await this.ensureConnected();
    const response = await sdk.getFriendList(true);
    const friends = unwrapSdkResponse(response, '联系人加载失败') || [];
    let fullInfo = [];
    const userIds = friends.map(friend => friend.userID).filter(Boolean);
    if (userIds.length) {
      fullInfo = await this.findFullUsers(userIds).catch(() => []);
    }
    const fullInfoMap = new Map(fullInfo.map(user => [user.userID, user]));
    return friends
      .map(friend => normalizeContact(friend, fullInfoMap.get(friend.userID)))
      .sort((a, b) => a.title.localeCompare(b.title, 'zh-CN'));
  }

  async refreshContactList() {
    if (!this.currentUser?.userId) {
      return [];
    }
    const contacts = await this.getServerContactList();
    this.contactList = contacts;
    this.emit('contactsChanged', contacts);
    return contacts;
  }

  async findFullUsers(userIds) {
    const ids = (Array.isArray(userIds) ? userIds : [userIds]).filter(Boolean);
    if (!ids.length || !this.currentUser?.chatToken) {
      return [];
    }
    const response = await axios.post(chatUrl('/user/find/full'), {
      userIDs: ids
    }, {
      withCredentials: false,
      headers: this.authHeaders()
    });
    const data = unwrapApiResponse(response, '用户资料加载失败');
    return data.users || [];
  }

  async searchUsers(keyword, limit = 20) {
    const value = String(keyword || '').trim();
    if (!value || !this.currentUser?.chatToken) {
      return [];
    }
    const response = await axios.post(chatUrl('/user/search/full'), {
      keyword: value,
      pagination: {
        pageNumber: 1,
        showNumber: limit
      },
      genders: 0,
      normal: 1
    }, {
      withCredentials: false,
      headers: this.authHeaders()
    });
    const data = unwrapApiResponse(response, '用户搜索失败');
    return data.users || [];
  }

  async resolveUser(keyword) {
    const value = normalizeContactKeyword(keyword);
    if (!value) {
      return null;
    }
    const managedBot = await this.resolveManagedBot(value).catch(() => null);
    if (managedBot?.userId) {
      return managedBot;
    }
    const sdk = await this.ensureConnected();
    const direct = await sdk.getUsersInfo([value]).then(response => {
      const users = unwrapSdkResponse(response, '用户加载失败') || [];
      return users.find(user => user.userID === value) || users.find(user => String(user?.userId || '').trim() === value) || null;
    }).catch(() => null);
    if (direct?.userID) {
      return normalizeUserProfile(direct, value);
    }
    const users = await this.searchUsers(value).catch(() => []);
    const normalizedValue = value.toLowerCase();
    const variants = new Set([
      normalizedValue,
      normalizedValue.replace(/^\+/, ''),
      value.replace(/^\+/, ''),
      value,
      value.replace(/^\+86/, ''),
      normalizeContactKeyword(normalizedValue)
    ]);
    const matched = users.find(user => {
      return matchByKeywords(user, normalizedValue, Array.from(variants).filter(Boolean));
    }) || null;
    return matched ? normalizeUserProfile(matched, value) : null;
  }

  async addContact(userId, keyword, reason = '') {
    const normalizedKeyword = normalizeContactKeyword(keyword);
    if (!normalizedKeyword) {
      throw new Error('请输入用户ID、手机号或用户名');
    }
    const sdk = await this.ensureConnected();
    let user = null;
    const directId = isUserIdLike(normalizedKeyword) ? normalizedKeyword : null;
    if (directId) {
      try {
        const exactUser = await this.resolveUser(directId);
        if (exactUser?.userId || exactUser?.uid) {
          user = exactUser;
        }
      } catch (error) {
        user = null;
      }
    } else {
      user = await this.resolveUser(keyword).catch(() => null);
    }
    const targetId = user?.userId || directId || normalizedKeyword;
    if (!targetId || targetId === userId) {
      throw new Error('请选择有效联系人');
    }
    const managedBotConnected = await this.connectManagedBot(targetId).catch(() => false);
    if (managedBotConnected) {
      const contacts = await this.refreshContactList().catch(() => this.contactList);
      const contact = contacts.find(item => String(item.uid || item.id || '') === targetId)
        || normalizeContact({
          userID: targetId,
          nickname: user?.displayName || targetId,
          faceURL: toPortableObjectPath(user?.portrait || ''),
          remark: ''
        }, user?.raw || null);
      return {
        targetId,
        contact,
        alreadyFriend: false,
        pending: false,
        managedBot: true
      };
    }
    let alreadyFriend = false;
    await sdk.addFriend({ toUserID: targetId, reqMsg: reason || '添加联系人' }).then(response => {
      unwrapSdkResponse(response, '添加联系人失败');
    }).catch(error => {
      const message = normalizeSdkError(error, '添加联系人失败');
      if (/black|block|拉黑|黑名单|拒绝添加/i.test(message)) {
        throw new Error('无法添加');
      }
      if (!/already|already.*friend|好友关系已存在|已经是好友|重复/i.test(message)) {
        throw new Error(message);
      }
      alreadyFriend = true;
    });
    const contacts = await this.refreshContactList().catch(() => this.contactList);
    const contact = contacts.find(item => String(item.uid || item.id || '') === targetId);
    if (contact) {
      return {
        targetId,
        contact,
        alreadyFriend,
        pending: false
      };
    }
    if (alreadyFriend) {
      return {
        targetId,
        contact: normalizeContact({
          userID: targetId,
          nickname: user?.displayName || targetId,
          faceURL: toPortableObjectPath(user?.portrait || ''),
          remark: ''
        }, user?.raw || null),
        alreadyFriend: true,
        pending: false
      };
    }
    return {
      targetId,
      user,
      alreadyFriend: false,
      pending: true
    };
  }

  async getReceivedFriendApplications(options = {}) {
    const sdk = await this.ensureConnected();
    const response = await sdk.getFriendApplicationListAsRecipient({
      handleResults: options.onlyUnhandled === false ? [] : [ApplicationHandleResult.Unprocessed],
      offset: Number(options.offset || 0),
      count: Number(options.count || 100)
    });
    const applications = unwrapSdkResponse(response, '好友申请加载失败') || [];
    return applications
      .map(item => normalizeFriendApplication(item))
      .filter(item => item.fromUserID)
      .sort((a, b) => Number(b.createTime || 0) - Number(a.createTime || 0));
  }

  async acceptFriendApplication(fromUserID, handleMsg = '已同意') {
    const sdk = await this.ensureConnected();
    const targetId = String(fromUserID || '').trim();
    if (!targetId) {
      throw new Error('缺少申请人ID');
    }
    unwrapSdkResponse(await sdk.acceptFriendApplication({
      toUserID: targetId,
      handleMsg
    }), '同意好友申请失败');
    await this.refreshContactList().catch(() => {});
    this.emit('friendApplicationsChanged', { fromUserID: targetId, handleResult: ApplicationHandleResult.Agree });
    return true;
  }

  async refuseFriendApplication(fromUserID, handleMsg = '已拒绝') {
    const sdk = await this.ensureConnected();
    const targetId = String(fromUserID || '').trim();
    if (!targetId) {
      throw new Error('缺少申请人ID');
    }
    unwrapSdkResponse(await sdk.refuseFriendApplication({
      toUserID: targetId,
      handleMsg
    }), '拒绝好友申请失败');
    this.emit('friendApplicationsChanged', { fromUserID: targetId, handleResult: ApplicationHandleResult.Reject });
    return true;
  }

  async updateContactAlias(userId, targetId, alias) {
    const sdk = await this.ensureConnected();
    unwrapSdkResponse(await sdk.updateFriends({
      friendUserIDs: [targetId],
      remark: alias
    }), '备注名修改失败');
    await this.refreshContactList().catch(() => {});
    return alias;
  }

  async removeContact(targetId, { block = false, deleteConversation = false, conversation = null } = {}) {
    const sdk = await this.ensureConnected();
    if (block) {
      unwrapSdkResponse(await sdk.addBlack({ toUserID: targetId, ex: '' }), '拉黑好友失败');
    } else {
      unwrapSdkResponse(await sdk.deleteFriend(targetId), '删除好友失败');
    }
    if (deleteConversation && conversation) {
      const conversationID = await this.getSdkConversationID(conversation);
      unwrapSdkResponse(await sdk.deleteConversationAndDeleteAllMsg(conversationID), '删除对话失败');
    }
    await this.refreshContactList().catch(() => {});
    await this.refreshConversationList().catch(() => {});
    return true;
  }

  createSingleConversation(userId, userInfo = null) {
    const title = getTitle(userInfo?.title || userInfo?.displayName || userInfo?.name, userId);
    return {
      id: conversationIdFor('single', userId),
      type: 'single',
      target: userId,
      title,
      subtitle: '',
      unread: 0,
      portrait: toPortableObjectPath(userInfo?.portrait || ''),
      avatarText: avatarText(title),
      updatedAt: Date.now(),
      raw: {
        conversationType: SessionType.Single,
        userID: userId,
        target: userId
      }
    };
  }

  normalizeGroup(group, myRole = '') {
    const title = getTitle(group?.groupName, group?.groupID);
    return {
      id: conversationIdFor('group', group?.groupID),
      type: 'group',
      target: group?.groupID,
      title,
      subtitle: group?.notification || group?.introduction || '',
      unread: 0,
      portrait: toPortableObjectPath(group?.faceURL || ''),
      avatarText: avatarText(title),
      updatedAt: Number(group?.createTime || Date.now()),
      role: myRole,
      raw: {
        conversationType: SessionType.Group,
        groupID: group?.groupID,
        role: myRole,
        group
      }
    };
  }

  async createGroup(userId, { name, memberIds = [], announcement = '', welcomeText = '' }) {
    const sdk = await this.ensureConnected();
    const groupName = String(name || '').trim();
    const members = Array.from(new Set(memberIds.map(id => String(id || '').trim()).filter(id => id && id !== userId)));
    if (!groupName) {
      throw new Error('请输入群组名称');
    }
    if (!members.length) {
      throw new Error('请至少选择一位联系人');
    }
    const { groupMaxMembers } = await this.getPlatformSettings();
    if (members.length + 1 > groupMaxMembers) {
      throw new Error(`群组人数不能超过 ${groupMaxMembers} 人（包含群主）`);
    }
    const response = await withTimeout(sdk.createGroup({
      memberUserIDs: members,
      ownerUserID: userId,
      groupInfo: {
        groupName,
        notification: String(announcement || '').trim(),
        introduction: String(welcomeText || '').trim(),
        groupType: GroupType.WorkingGroup,
        needVerification: GroupVerificationType.AllNot
      }
    }), 20000, '创建群组超时，请检查连接后重试');
    const group = unwrapSdkResponse(response, '创建群组失败');
    if (!group?.groupID) {
      throw new Error('服务器未返回群组ID，创建失败');
    }
    const conversation = this.normalizeGroup(group, 'owner');
    const welcome = String(welcomeText || '').trim();
    if (welcome) {
      const welcomeMessage = await this.sendTextMessage(conversation, welcome);
      conversation.welcomeMessageFailed = welcomeMessage?.status === 'failed';
    }
    await this.refreshConversationList().catch(() => {});
    return conversation;
  }

  async joinGroup(userId, groupKey) {
    const sdk = await this.ensureConnected();
    const value = String(groupKey || '').trim();
    let groupId = value;
    const search = await sdk.searchGroups({
      keywordList: [value],
      isSearchGroupID: true,
      isSearchGroupName: true
    }).then(response => unwrapSdkResponse(response, '群组搜索失败')).catch(() => []);
    const matched = search.find(group => group.groupID === value || group.groupName === value) || search[0];
    if (matched?.groupID) {
      groupId = matched.groupID;
    }
    unwrapSdkResponse(await sdk.joinGroup({
      groupID: groupId,
      reqMsg: '申请加入群组',
      joinSource: GroupJoinSource.Search
    }), '加入群组失败');
    const group = matched || (await this.getRawGroup(groupId));
    return this.normalizeGroup(group || { groupID: groupId, groupName: groupId }, 'member');
  }

  async broadcastMessage(userId, targetIds, text) {
    let successCount = 0;
    let failedCount = 0;
    for (const targetId of targetIds || []) {
      try {
        await this.sendTextMessage(this.createSingleConversation(targetId), text);
        successCount += 1;
      } catch (error) {
        failedCount += 1;
      }
    }
    return { successCount, failedCount };
  }

  async getRawGroup(groupId) {
    const sdk = await this.ensureConnected();
    const groups = unwrapSdkResponse(await sdk.getSpecifiedGroupsInfo([groupId]), '群资料加载失败') || [];
    return groups[0] || null;
  }

  async getGroupDetail(userId, groupId) {
    const group = await this.getRawGroup(groupId);
    const members = await this.getGroupMembers(userId, groupId).catch(() => []);
    const me = members.find(member => member.uid === userId);
    const messageActionSettings = messageActionSettingsFromGroup(group);
    return {
      id: group?.groupID || groupId,
      groupId: group?.groupID || groupId,
      name: group?.groupName || groupId,
      announcement: group?.notification || '',
      welcomeText: group?.introduction || '',
      portrait: toPortableObjectPath(group?.faceURL || ''),
      ownerId: group?.ownerUserID || '',
      memberCount: Number(group?.memberCount || members.length || 0),
      myRole: me?.role || (group?.ownerUserID === userId ? 'owner' : 'member'),
      myAlias: me?.nickname || '',
      muteSetting: {
        mode: Number(group?.status) === 3 ? 'all' : 'off',
        scheduleType: 'once'
      },
      messageActionSettings,
      raw: group
    };
  }

  async getGroupMessageActionSettings(groupId) {
    const group = await this.getRawGroup(groupId);
    return messageActionSettingsFromGroup(group);
  }

  async getGroupMembers(userId, groupId) {
    const sdk = await this.ensureConnected();
    const response = await sdk.getGroupMemberList({
      groupID: groupId,
      filter: GroupMemberFilter.All,
      offset: 0,
      count: 1000
    });
    const members = unwrapSdkResponse(response, '群员加载失败') || [];
    return members.map(member => ({
      uid: member.userID,
      id: member.userID,
      displayName: member.nickname || member.userID,
      nickname: member.nickname || member.userID,
      portrait: toPortableObjectPath(member.faceURL || ''),
      role: roleLevelToText(member.roleLevel),
      roleLevel: member.roleLevel,
      mutedUntil: Number(member.muteEndTime || 0),
      raw: member
    }));
  }

  async updateGroupAlias(userId, groupId, alias) {
    const sdk = await this.ensureConnected();
    unwrapSdkResponse(await sdk.setGroupMemberInfo({
      groupID: groupId,
      userID: userId,
      nickname: alias
    }), '本群昵称保存失败');
    return alias;
  }

  async leaveGroup(userId, groupId) {
    const sdk = await this.ensureConnected();
    unwrapSdkResponse(await sdk.quitGroup(groupId), '退出群组失败');
    return {};
  }

  async dismissGroup(userId, groupId) {
    const sdk = await this.ensureConnected();
    unwrapSdkResponse(await sdk.dismissGroup(groupId), '解散群组失败');
    return {};
  }

  async transferGroup(userId, groupId, targetUserId) {
    const sdk = await this.ensureConnected();
    unwrapSdkResponse(await sdk.transferGroupOwner({
      groupID: groupId,
      newOwnerUserID: targetUserId
    }), '转让群组失败');
    return {};
  }

  async setGroupAdmins(userId, groupId, adminIds) {
    const sdk = await this.ensureConnected();
    const selected = new Set(adminIds || []);
    const members = await this.getGroupMembers(userId, groupId);
    await Promise.all(members
      .filter(member => member.role !== 'owner' && member.uid !== userId)
      .map(member => sdk.setGroupMemberInfo({
        groupID: groupId,
        userID: member.uid,
        roleLevel: selected.has(member.uid) ? GroupMemberRole.Admin : GroupMemberRole.Normal
      }).then(response => unwrapSdkResponse(response, '管理员设置失败')).catch(() => null)));
    return {};
  }

  async inviteGroupMembers(groupId, userIds) {
    const sdk = await this.ensureConnected();
    const invited = Array.from(new Set((userIds || []).map(id => String(id || '').trim()).filter(Boolean)));
    const [{ groupMaxMembers }, group] = await Promise.all([
      this.getPlatformSettings(),
      this.getRawGroup(groupId)
    ]);
    const currentCount = Math.max(0, Number(group?.memberCount) || 0);
    if (currentCount + invited.length > groupMaxMembers) {
      throw new Error(`群组人数不能超过 ${groupMaxMembers} 人（当前 ${currentCount} 人）`);
    }
    unwrapSdkResponse(await sdk.inviteUserToGroup({ groupID: groupId, reason: '邀请加入群组', userIDList: invited }), '添加群员失败');
    return {};
  }

  async muteGroupMember(groupId, userId, mutedSeconds) {
    const sdk = await this.ensureConnected();
    unwrapSdkResponse(await sdk.changeGroupMemberMute({ groupID: groupId, userID: userId, mutedSeconds: Math.max(0, Number(mutedSeconds || 0)) }), '群员禁言设置失败');
    return {};
  }

  async updateGroupProfile(userId, groupId, { name, announcement, welcomeText }) {
    const sdk = await this.ensureConnected();
    unwrapSdkResponse(await sdk.setGroupInfo({
      groupID: groupId,
      groupName: name,
      notification: announcement,
      introduction: welcomeText
    }), '群资料保存失败');
    return {
      name,
      announcement,
      welcomeText,
      updatedAt: Date.now()
    };
  }

  async updateGroupMessageActionSettings(groupId, { recallLimitMinutes, editLimitMinutes }) {
    const sdk = await this.ensureConnected();
    const group = await this.getRawGroup(groupId);
    const extension = parseGroupExtension(group?.ex || '');
    const settings = {
      recallLimitMinutes: normalizeMessageActionLimit(recallLimitMinutes),
      editLimitMinutes: normalizeMessageActionLimit(editLimitMinutes)
    };
    extension[JC_GROUP_MESSAGE_ACTION_SETTINGS_KEY] = settings;
    unwrapSdkResponse(await sdk.setGroupInfo({
      groupID: groupId,
      ex: serializeGroupExtension(extension)
    }), '群消息时限保存失败');
    return {
      configured: true,
      ...settings
    };
  }

  async resetGroupMessageActionSettings(groupId) {
    const sdk = await this.ensureConnected();
    const group = await this.getRawGroup(groupId);
    const extension = parseGroupExtension(group?.ex || '');
    delete extension[JC_GROUP_MESSAGE_ACTION_SETTINGS_KEY];
    unwrapSdkResponse(await sdk.setGroupInfo({
      groupID: groupId,
      ex: serializeGroupExtension(extension)
    }), '恢复全局消息时限失败');
    return {
      configured: false,
      recallLimitMinutes: 0,
      editLimitMinutes: 0
    };
  }

  async kickGroupMembers(userId, groupId, targetIds) {
    const sdk = await this.ensureConnected();
    const removedIds = (targetIds || []).filter(Boolean);
    unwrapSdkResponse(await sdk.kickGroupMember({
      groupID: groupId,
      reason: '移出群组',
      userIDList: removedIds
    }), '踢人失败');
    return { removedIds };
  }

  async updateGroupMuteSetting(userId, groupId, setting) {
    const sdk = await this.ensureConnected();
    const mode = setting?.mode || 'off';
    unwrapSdkResponse(await sdk.changeGroupMute({
      groupID: groupId,
      isMute: mode !== 'off'
    }), '禁言设置保存失败');
    return {
      mode,
      scheduleType: setting?.scheduleType || 'once',
      startsAtMs: Number(setting?.startsAtMs || 0),
      endsAtMs: Number(setting?.endsAtMs || 0),
      dailyStart: setting?.dailyStart || '09:00',
      dailyEnd: setting?.dailyEnd || '18:00'
    };
  }

  async getProfile(userId) {
    const sdk = await this.ensureConnected();
    if (userId === this.currentUser?.userId) {
      const user = unwrapSdkResponse(await sdk.getSelfUserInfo(), '用户资料加载失败');
      let profile = normalizeUserProfile(user, userId);
      if (!profile.portrait) {
        const users = unwrapSdkResponse(await sdk.getUsersInfo([userId]), '用户资料加载失败') || [];
        if (users[0]) {
          profile = normalizeUserProfile(users[0], userId);
        }
      }
      this.currentUser = {
        ...this.currentUser,
        displayName: profile.displayName || this.currentUser?.displayName || userId,
        portrait: profile.portrait || this.currentUser?.portrait || ''
      };
      return profile;
    }
    const users = unwrapSdkResponse(await sdk.getUsersInfo([userId]), '用户资料加载失败') || [];
    if (users[0]) {
      return normalizeUserProfile(users[0], userId);
    }
    const full = await this.findFullUsers([userId]).catch(() => []);
    return full[0] ? normalizeUserProfile(full[0], userId) : null;
  }

  async updateProfile(userId, { displayName, portraitFile }) {
    const sdk = await this.ensureConnected();
    let faceURL = toPortableObjectPath(this.currentUser?.portrait || '');
    if (portraitFile) {
      const avatarBytes = await portraitFile.arrayBuffer();
      const avatarFile = new File([avatarBytes], `avatar-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      const response = await sdk.uploadFile({
        name: avatarFile.name,
        contentType: avatarFile.type,
        uuid: randomId('avatar-'),
        file: avatarFile
      });
      faceURL = toPortableObjectPath(unwrapSdkResponse(response, '头像上传失败')?.url || faceURL);
    }
    const profile = {
      userID: userId,
      nickname: displayName,
      faceURL
    };
    unwrapSdkResponse(await sdk.setSelfInfo(profile), '用户资料保存失败');
    if (this.currentUser?.chatToken) {
      await axios.post(chatUrl('/user/update'), {
        userID: userId,
        nickname: { value: displayName },
        faceURL: faceURL ? { value: faceURL } : undefined
      }, {
        withCredentials: false,
        headers: this.authHeaders()
      }).catch(() => {});
    }
    return normalizeUserProfile(profile, userId);
  }

  async changePassword({ oldPassword, newPassword }) {
    const response = await axios.post(chatUrl('/account/password/change'), {
      userID: this.currentUser?.userId || '',
      currentPassword: passwordForOpenIM(oldPassword),
      newPassword: passwordForOpenIM(newPassword)
    }, {
      withCredentials: false,
      headers: this.authHeaders()
    });
    unwrapApiResponse(response, '密码修改失败');
    return true;
  }

  async resetPasswordWithCode({ mobile, resetCode, newPassword }) {
    const phone = parsePhoneAccount(mobile);
    const response = await axios.post(chatUrl('/account/password/reset'), {
      areaCode: phone.areaCode,
      phoneNumber: phone.phoneNumber,
      verifyCode: resetCode,
      password: passwordForOpenIM(newPassword)
    }, {
      withCredentials: false,
      headers: this.operationHeaders()
    });
    unwrapApiResponse(response, '密码设置失败');
    return true;
  }

  on(eventName, handler) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName).add(handler);
    return () => this.off(eventName, handler);
  }

  off(eventName, handler) {
    this.listeners.get(eventName)?.delete(handler);
  }

  emit(eventName, payload) {
    this.listeners.get(eventName)?.forEach(handler => handler(payload));
  }
}

export const imClient = new OpenIMClient();
